import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { formatINR, useSEO } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function Cart() {
  useSEO("Cart");
  const { items, updateQty, removeItem, total } = useCart();
  const navigate = useNavigate();
  const shipping = total >= 4999 ? 0 : (total > 0 ? 199 : 0);

  return (
    <div data-testid="cart-page" className="pt-32 pb-28">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] mb-12">Your Bag</h1>

        {items.length === 0 ? (
          <div className="text-center py-24" data-testid="cart-page-empty">
            <ShoppingBag size={40} className="mx-auto mb-6 text-black/20" />
            <p className="text-muted-foreground mb-6">Your bag is empty.</p>
            <Link to="/shop" className="inline-block bg-black text-white rounded-full px-8 py-3.5 uppercase text-xs tracking-[0.2em]">Start shopping</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-6">
              {items.map((it) => (
                <div key={it.slug} className="flex gap-5 border-b border-black/10 pb-6" data-testid={`cart-row-${it.slug}`}>
                  <Link to={`/product/${it.slug}`} className="w-28 h-32 bg-[#F5F5F5] rounded-2xl overflow-hidden shrink-0">
                    <img src={`${it.image}?w=300&q=80`} alt={it.name} loading="lazy" className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 flex flex-col justify-between">
                    <div className="flex justify-between">
                      <div>
                        <Link to={`/product/${it.slug}`} className="font-display text-2xl">{it.name}</Link>
                        <p className="text-sm text-muted-foreground mt-1">{formatINR(it.price)}</p>
                      </div>
                      <button onClick={() => removeItem(it.slug)} data-testid={`cart-row-remove-${it.slug}`} className="text-muted-foreground hover:text-black h-fit"><X size={18} /></button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 border border-black/15 rounded-full">
                        <button onClick={() => updateQty(it.slug, it.quantity - 1)} data-testid={`cart-row-dec-${it.slug}`} className="w-9 h-9 flex items-center justify-center"><Minus size={13} /></button>
                        <span className="w-6 text-center text-sm">{it.quantity}</span>
                        <button onClick={() => updateQty(it.slug, it.quantity + 1)} data-testid={`cart-row-inc-${it.slug}`} className="w-9 h-9 flex items-center justify-center"><Plus size={13} /></button>
                      </div>
                      <span className="text-sm">{formatINR(it.price * it.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="lg:col-span-4">
              <div className="bg-[#F5F5F5] rounded-3xl p-8 sticky top-24">
                <h2 className="font-display text-2xl mb-6">Summary</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatINR(total)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
                  {shipping > 0 && <p className="text-xs text-muted-foreground">Add {formatINR(4999 - total)} more for free shipping.</p>}
                  <div className="flex justify-between text-lg pt-4 border-t border-black/10">
                    <span className="uppercase tracking-widest">Total</span>
                    <span data-testid="cart-page-total">{formatINR(total + shipping)}</span>
                  </div>
                </div>
                <button onClick={() => navigate("/checkout")} data-testid="cart-page-checkout"
                  className="w-full mt-6 bg-black text-white rounded-full py-4 uppercase text-xs tracking-[0.2em] hover:bg-black/85 transition-colors">
                  Secure checkout
                </button>
                <Link to="/shop" className="block text-center mt-4 text-xs uppercase tracking-widest link-underline">Continue shopping</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
